import { Color3, Mesh, MeshBuilder, PointLight, Texture, TransformNode, Vector3, type Scene } from '@babylonjs/core';
import { FireMaterial } from '@babylonjs/materials/fire/fireMaterial';

const FIRE_TEXTURE_ROOT = '/assets/vfx/fire/';
/**
 * Fraction of the billboard's height that the flame silhouette fills; the rest is empty headroom. FireMaterial's shader
 * doesn't draw its textures as-is: it shifts them up by 0.2 of the quad's height (a fixed `vDiffuseUV.y -= 0.2` in its vertex
 * shader) and wobbles the lookup by up to ~0.19 with scrolling noise. A tip drawn near the image's top would be pushed past
 * the quad's edge and cut flat, so the flame is kept low in the image. Must match `game-content/scripts/generate-flame-opacity.mjs`.
 */
const FLAME_FILL_FRACTION = 0.72;
/** How far the flame's base sinks below `position`, as a fraction of its height. The shader's fixed 0.2 upward shift (see FLAME_FILL_FRACTION) otherwise leaves the flame floating above whatever it burns in. */
const BASE_SINK_FRACTION = 0.15;
const LIGHT_COLOR = new Color3(1, 0.55, 0.2);

export interface BrazierFireOptions {
    /** Scene-space position of the flame's base. */
    readonly position: Vector3;
    /** Width of the flame, in scene units. */
    readonly width: number;
    /** Height of the flame, in scene units (the billboard itself is taller, to leave room for the flame to flicker upward). */
    readonly height: number;
    /** How fast the flame flickers and rises. 1 is calm and slow, 5 is the FireMaterial sample's lively default. */
    readonly flameSpeed: number;
    /** Brightness of the flame itself (not the light it casts). 1 is the texture's native brightness, which the shader already doubles, so it reads as very bright; lower values give a dimmer, more orange flame. */
    readonly flameBrightness: number;
    /** Intensity of the warm light cast around the fire. 0 disables the light. */
    readonly lightIntensity: number;
    /** Distance the fire's light reaches, in scene units. */
    readonly lightRange: number;
    /** Starts the flame this far into its animation, so several fires burn on different cycles instead of flickering in lockstep. */
    readonly timeOffsetSeconds?: number;
    /** Offsets this fire's light flicker so several fires don't pulse in unison. */
    readonly flickerPhase?: number;
}

export interface BrazierFire {
    readonly root: TransformNode;
    /** Flickers its light; its owner calls it every frame. */
    update(deltaSeconds: number): void;
    dispose(): void;
}

/**
 * An animated flame, made with Babylon's `FireMaterial` (see
 * `@babylonjs/materials`) on a Y-billboarded plane, with a warm point light
 * that gently flickers so the fire visibly lights the space around it.
 * Textures live in `game-content/assets/vfx/fire/` (see that folder's
 * README section for where each comes from).
 *
 * The flicker is a fixed sum of sines rather than random noise so the effect
 * is deterministic and needs no RNG.
 */
export function createBrazierFire(scene: Scene, options: BrazierFireOptions): BrazierFire {
    const root = new TransformNode('brazier-fire', scene);
    root.position.copyFrom(options.position);

    const planeHeight = options.height / FLAME_FILL_FRACTION;
    const plane = MeshBuilder.CreatePlane('brazier-fire-flame', { width: options.width, height: planeHeight }, scene);
    plane.parent = root;
    // The plane's origin is its center; lift it so `position` is the flame's base (less the sink).
    plane.position.y = planeHeight / 2 - options.height * BASE_SINK_FRACTION;
    plane.billboardMode = Mesh.BILLBOARDMODE_Y;
    plane.isPickable = false;

    const material = new FireMaterial('brazier-fire-material', scene);
    // The shader scrolls the distortion texture to make the flame flicker, so that
    // one must wrap. The flame and its mask are sampled at shifted, wobbling
    // coordinates (the bottom fifth of the quad reads below the image); wrapping
    // them would draw the flame's tip again at its base, so they clamp instead.
    const diffuseTexture = new Texture(`${FIRE_TEXTURE_ROOT}fire.png`, scene);
    diffuseTexture.wrapU = Texture.CLAMP_ADDRESSMODE;
    diffuseTexture.wrapV = Texture.CLAMP_ADDRESSMODE;
    diffuseTexture.level = options.flameBrightness;
    const opacityTexture = new Texture(`${FIRE_TEXTURE_ROOT}flameOpacity.png`, scene);
    opacityTexture.wrapU = Texture.CLAMP_ADDRESSMODE;
    opacityTexture.wrapV = Texture.CLAMP_ADDRESSMODE;
    material.diffuseTexture = diffuseTexture;
    material.distortionTexture = new Texture(`${FIRE_TEXTURE_ROOT}distortion.png`, scene);
    material.opacityTexture = opacityTexture;
    material.speed = options.flameSpeed;
    if (options.timeOffsetSeconds) {
        // FireMaterial has no public way to set its animation clock: it adds each
        // frame's delta to `_lastTime` (milliseconds) and feeds that to the shader as
        // `time`. Seeding it starts this flame part-way through its cycle. The field
        // is private, so this relies on the exact version pinned in
        // packages/engine/package.json (@babylonjs/materials 9.22.1).
        (material as unknown as { _lastTime: number })._lastTime = options.timeOffsetSeconds * 1000;
    }
    plane.material = material;

    let light: PointLight | null = null;
    if (options.lightIntensity > 0) {
        const pointLight = new PointLight('brazier-fire-light', new Vector3(0, options.height * 0.4, 0), scene);
        pointLight.parent = root;
        pointLight.diffuse = LIGHT_COLOR;
        pointLight.specular = LIGHT_COLOR;
        pointLight.range = options.lightRange;
        pointLight.intensity = options.lightIntensity;
        light = pointLight;
    }

    const phase = options.flickerPhase ?? 0;
    let elapsedSeconds = 0;
    return {
        root,
        update(deltaSeconds: number): void {
            if (!light) return;
            elapsedSeconds += deltaSeconds;
            const flicker = 0.88 + 0.08 * Math.sin(elapsedSeconds * 7.3 + phase) + 0.05 * Math.sin(elapsedSeconds * 17.9 + phase * 2.1);
            light.intensity = options.lightIntensity * flicker;
        },
        dispose(): void {
            light?.dispose();
            // Also disposes the FireMaterial and, via `true`, its textures.
            plane.dispose(false, true);
            root.dispose();
        },
    };
}
